import { useTranslation } from '../../../hooks/useTranslation';
import { Box, Button, Card, CardActionArea, Grid, Typography } from '@mui/material';
import { useState } from 'react';

interface CountryOption {
    id: string;
    name: string;
    translationKey: string;
    available: boolean;
    flagCode: string;
}

const countries: CountryOption[] = [
    { id: 'Germany', translationKey: 'germany', name: 'Germany', available: true, flagCode: 'DE' },
    { id: 'Austria', translationKey: 'austria', name: 'Austria', available: false, flagCode: 'AT' },
    { id: 'Switzerland', translationKey: 'switzerland', name: 'Switzerland', available: false, flagCode: 'CH' },
    { id: 'United States', translationKey: 'unitedStates', name: 'United States', available: false, flagCode: 'US' },
    { id: 'United Kingdom', translationKey: 'unitedKingdom', name: 'United Kingdom', available: false, flagCode: 'GB' },
    { id: 'France', translationKey: 'france', name: 'France', available: false, flagCode: 'FR' },
];

interface CountrySelectionProps {
    selectedCountry: string;
    onCountryChange: (country: string) => void;
    onNext: () => void;
}

const CountrySelection = ({ selectedCountry, onCountryChange, onNext }: CountrySelectionProps) => {
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);

    const handleCountrySelect = (country: CountryOption) => {
        if (!country.available) {
            setError(t('onboarding.country.error'));
            return;
        }

        setError(null);
        onCountryChange(country.id);
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            height: '100%',
            width: '100%',
            maxWidth: 800,
            margin: '0 auto',
        }}>

            {error && (
                <Box sx={{
                    backgroundColor: 'rgba(211, 47, 47, 0.2)',
                    color: '#f44336',
                    padding: 2,
                    borderRadius: 1,
                    marginBottom: 2,
                    border: '1px solid rgba(211, 47, 47, 0.5)'
                }}>
                    <Typography variant="body2">{error}</Typography>
                </Box>
            )}

            <Grid container spacing={2} sx={{ mt: 8 }}>
                {countries.map((country) => (
                    <Grid item xs={12} sm={6} md={4} key={country.id}>
                        <Card
                            sx={{
                                position: 'relative',
                                borderColor: selectedCountry === country.id ? '#FF5722' : 'rgba(255, 255, 255, 0.1)',
                                borderWidth: 2,
                                borderStyle: 'solid',
                                opacity: 1,
                                transition: 'all 0.2s ease-in-out',
                                backgroundColor: !country.available ? 'rgba(0, 0, 0, 0.12)' : 'transparent',
                                '&:hover': {
                                    borderColor: country.available ? '#FF5722' : 'rgba(255, 255, 255, 0.1)',
                                    backgroundColor: country.available ? 'rgba(255, 87, 34, 0.05)' : 'rgba(0, 0, 0, 0.12)',
                                },
                            }}
                        >
                            {!country.available && (
                                <Box sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    color: 'white',
                                    padding: '4px 8px',
                                    textAlign: 'center',
                                    fontSize: '0.75rem',
                                    zIndex: 1,
                                    fontWeight: 500,
                                }}>
                                    {t('onboarding.country.comingSoon')}
                                </Box>
                            )}
                            <CardActionArea
                                onClick={() => handleCountrySelect(country)}
                                disabled={!country.available}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    padding: 3,
                                    height: '100%'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 30,
                                        marginBottom: 1,
                                        marginTop: 1,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        overflow: 'hidden',
                                        borderRadius: 1,
                                        border: '1px solid rgba(255, 255, 255, 0.1)'
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={`https://flagcdn.com/w40/${country.flagCode.toLowerCase()}.png`}
                                        alt={`${country.name} flag`}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                    {t(`onboarding.country.${country.translationKey}`)}
                                </Typography>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 'auto',
                paddingTop: 3
            }}>
                <Button
                    variant="contained"
                    onClick={onNext}
                    disabled={!selectedCountry}
                    sx={{
                        minWidth: 120,
                        height: 42,
                    }}
                >
                    {t('onboarding.country.next')}
                </Button>
            </Box>
        </Box>
    );
};

export default CountrySelection;
